import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import { NhsNumberSchema } from "@thalamos/common";
import type {
  BasePatientDemographics,
  CreatePatientFromExternalIdRefusal,
  ExternalMappedDemographics,
  ExternalPatientLink,
  ExternalPatientLinkEnhanced,
  Patient,
} from "@thalamos/aspire-common";
import { createPatientFromExternalIdRefusalCodes } from "@thalamos/aspire-common";

import { api } from "../../api.js";
import type { DefaultPageProps } from "../defaultProps.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
  ReviewNewExternalPatientRecord,
  SectionTitle,
} from "~/components/design-system/index.js";
import { routeFns } from "~/routePaths.js";
import { logActionToDatadog, logErrorToDatadog } from "~/tracing.js";

// Holds the state for this page.
type State =
  | {
      // Something has gone wrong and needs presenting to the user.
      type: "error";
      error: string;
    }
  | {
      // The patient details were not found on the external system.
      type: "not-found";
    }
  | {
      // The patient already exists in eMHA, and we need to review their details.
      type: "externalPatientLink";
      externalPatientLink: ExternalPatientLink | undefined;
      rootPatientId: string;
    }
  | {
      // The patient already exists in eMHA, but they have been merged into another patient,
      // and this link is not the higest priority.
      type: "mergedExternalPatientLink";
      rootPatientId: string;
    }
  | {
      // The patient does not exist in eMHA, and we need to create them.
      type: "newPatient";
      demographics: BasePatientDemographics & {
        nhsNumber?: string;
        deathNotificationStatus: Patient["deathNotificationStatus"];
      };
      externalSystemType: ExternalPatientLinkEnhanced["externalSystemType"];
      externalSystemId: ExternalPatientLinkEnhanced["externalSystemId"];
      externalPatientIdType: ExternalPatientLinkEnhanced["externalPatientIdType"];
      externalPatientIdValue: ExternalPatientLinkEnhanced["externalPatientIdValue"];
    };

function reasonIn(data: unknown): string | undefined {
  return typeof data === "object" &&
    data !== null &&
    "reason" in data &&
    typeof data.reason === "string" &&
    data.reason.trim() !== ""
    ? data.reason
    : undefined;
}

function isRefusal(data: unknown): data is CreatePatientFromExternalIdRefusal {
  return (
    typeof data === "object" &&
    data !== null &&
    "code" in data &&
    createPatientFromExternalIdRefusalCodes.some(
      (code) => code === data.code,
    ) &&
    reasonIn(data) !== undefined
  );
}

export function PatientContextLaunchPage({}: DefaultPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creatingPatient, setCreatingPatient] = useState(false);

  // Extract the query params
  const { search } = useLocation();
  const qs = new URLSearchParams(search);

  const token: string | null = qs.get("token");
  const redirectTo = qs.get("redirectTo");
  const source: string | null = qs.get("source");
  // Set on the pre-login clickthrough interstitial when the clinician opted in
  // to being remembered; the server binds the EPR identity at redeem.
  const rememberLogin = qs.get("rememberLogin") === "true";
  // TPP and the generic EPR clickthrough are both PDS-sourced: the server
  // creates a PDS link (not an EPR-specific link), so the existing-patient
  // lookup must find the patient by their PDS link rather than an EPR one.
  const usesPdsLink = source === "tpp" || source === "generic";

  // Only run this check once - if the token is missing after initial component
  // mount then it is probably due to a navigation race condition as the user
  // leaves the page
  useEffect(() => {
    if (!token) {
      throw new Error(
        "Token missing from URL - please try your search / clickthrough again",
      );
    }
  }, []);

  const [state, setState] = useState<State | undefined>(undefined);

  // Look for an existing patient with this link
  useEffect(() => {
    const doEffect = async () => {
      if (token) {
        // Redeem the token to get the external system details
        const redeemTokenResponse =
          source === "generic"
            ? await api.generic.clickThrough.redeemToken({
                token,
                rememberLogin,
              })
            : source === "tpp"
              ? await api.tpp.clickThrough.redeemToken({ token, rememberLogin })
              : await api.rio.clickThrough.redeemToken({
                  token,
                  rememberLogin,
                });

        if (redeemTokenResponse.status !== 200) {
          logActionToDatadog(
            "PatientContextLaunchPage - failed to redeem token",
            {
              token,
              source: source ?? "rio",
              status: redeemTokenResponse.status,
              data: redeemTokenResponse.data,
            },
          );

          setState({
            type: "error",
            error:
              "Failed to complete operation - please try your search / clickthrough again from the start",
          });
          return;
        }

        const {
          externalSystemId,
          externalSystemType,
          externalPatientIdType,
          externalPatientIdValue,
        } = redeemTokenResponse.data;

        // Get the patient details from the external system
        // by attemting a create with confirmCreate = false
        const res = await api.patients.createFromExternalId({
          externalSystemType,
          externalSystemId,
          externalPatientIdType,
          externalPatientIdValue: [externalPatientIdValue],
        });

        if (res.status === 200) {
          if (res.data.result === "new-patient") {
            logActionToDatadog(
              "PatientContextLaunchPage - new patient shown for review",
            );
            setState({
              type: "newPatient",
              demographics: res.data.demographics,
              externalSystemType,
              externalSystemId,
              externalPatientIdType,
              externalPatientIdValue: [externalPatientIdValue],
            });
          } else if (res.data.result === "existing-patient") {
            logActionToDatadog("PatientContextLaunchPage - existing patient");

            if (res.data.isHighestPriorityNhsNumber) {
              // For TPP / generic there is no EPR-specific external link — the
              // server creates a PDS link instead (neither has a demographics
              // API). Find the PDS link by NHS number so we can trigger a sync.
              const externalPatientLink = usesPdsLink
                ? res.data.existingPatient.externalLinks!.find(
                    (l) =>
                      l.externalSystemType === "pds" &&
                      l.externalPatientIdValue.includes(externalPatientIdValue),
                  )
                : res.data.existingPatient.externalLinks!.find(
                    (l) =>
                      l.externalSystemId === externalSystemId &&
                      l.externalSystemType === externalSystemType &&
                      l.externalPatientIdValue.includes(externalPatientIdValue),
                  );

              setState({
                type: "externalPatientLink",
                externalPatientLink,
                rootPatientId: res.data.existingPatient.id,
              });
            } else {
              setState({
                type: "mergedExternalPatientLink",
                rootPatientId: res.data.existingPatient.id,
              });
            }
          }
        } else if (res.status === 404) {
          logActionToDatadog("PatientContextLaunchPage - patient not found");
          setState({ type: "not-found" });
          if (redirectTo) {
            setTimeout(() => {
              navigate(redirectTo, { replace: true });
            }, 1000);
          }
        } else {
          const refusal =
            res.status === 400 && isRefusal(res.data) ? res.data : undefined;

          if (refusal) {
            // A refusal is eMHA working as designed, so it is not an error.
            logActionToDatadog("PatientContextLaunchPage - launch refused", {
              code: refusal.code,
              externalSystemType,
            });
          } else {
            logErrorToDatadog(
              new Error(
                "PatientContextLaunchPage - failed to fetch patient from external system",
              ),
            );
          }
          setState({
            type: "error",
            error:
              reasonIn(res.data) ??
              "Failed to fetch patient details from external system",
          });

          if (redirectTo) {
            setTimeout(() => {
              navigate(redirectTo, { replace: true });
            }, 1000);
          }
        }
      }
    };

    doEffect();
  }, [token]);

  // If we have an existing link, do a pull and review
  useEffect(() => {
    const doEffect = async () => {
      if (state?.type === "externalPatientLink") {
        // Patient has an NHS number but isn’t linked to the org’s RiO.
        // If no externalPatientLink is found, show a warning.
        // Prevents silent failure when patient isn’t in RiO.
        // ticket number here - DEV-938
        if (!state.externalPatientLink) {
          renderErrorToast({
            message: t("components.externalPatientLink.createLinkButton.error"),
          });
        }
        navigate(
          state.externalPatientLink
            ? routeFns.patientSyncExternalLink(
                state.rootPatientId,
                state.externalPatientLink.id,
              )
            : routeFns.patientHome(state.rootPatientId),
          { replace: true },
        );
      }
    };

    doEffect();
  }, [state, navigate]);

  // If it is a new patient we will need to let them review the details.
  // We can't re-use the code within syncExternalPatientLink() because the
  // user may reject the patient details, which results in no eMHA patient
  // record or patient link being created.
  const patientRecords = useMemo(
    () =>
      state?.type === "newPatient"
        ? {
            Other: {
              ...state.demographics,
              dateOfBirth: state.demographics.dateOfBirth ?? null,
              restricted: false,
              deceasedDateTime: null,
              registeredGpOdsCode: null,
              pdsOfficialDocumentGender: null,
              ethnicity: null,
              outcome: "success",
            } satisfies ExternalMappedDemographics,
          }
        : undefined,
    [state],
  );

  const NHSNumber = useMemo(
    () =>
      state?.type === "newPatient" ? state.demographics.nhsNumber : undefined,
    [state],
  );

  // Create this id outside of the handler so that multiple clicks of
  // the button do not result in multiple links being created.
  const newExternalPatientLinkId = useMemo(() => v4(), []);

  // This is called when the user confirms the review dialog
  const onConfirm = useCallback(async () => {
    if (!token || state?.type !== "newPatient") {
      return false;
    }
    setCreatingPatient(true);
    try {
      // Call the create API again, confirming the creation of the brand
      // new patient
      const result = await api.patients.createFromExternalId({
        externalSystemType: state.externalSystemType,
        externalSystemId: state.externalSystemId,
        externalPatientIdType: state.externalPatientIdType,
        externalPatientIdValue: state.externalPatientIdValue,
        confirmCreate: true,
      });

      if (result.status !== 200 || result.data.result !== "patient-created") {
        renderErrorToast({
          message: t(
            "components.externalPatientLink.reviewNewExternalPatientRecord.toast.failedToCreatePatient",
          ),
        });
        return false;
      }

      logActionToDatadog(
        "PatientContextLaunchPage - patient created from external system",
        {
          patientId: result.data.createdPatient.id,
        },
      );

      // Navigate to the patient page`
      navigate(routeFns.patientHome(result.data.createdPatient.id), {
        replace: true,
      });

      return true;
    } finally {
      // However we terminate, we should mark the patient as not being created
      setCreatingPatient(false);
    }
  }, [navigate, newExternalPatientLinkId, state, t, token]);

  // This is called when the user cancels the review dialog
  const onCancel = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const titleText = t(
    state?.type === "mergedExternalPatientLink"
      ? "components.externalPatientLink.reviewNewExternalPatientRecord.title.mergedExternalPatientLink"
      : state?.type === "externalPatientLink"
        ? "components.externalPatientLink.reviewNewExternalPatientRecord.title.externalPatientLink"
        : state?.type === "newPatient"
          ? "components.externalPatientLink.reviewNewExternalPatientRecord.title.newPatient"
          : "components.externalPatientLink.reviewNewExternalPatientRecord.title.contacting",
  );

  const isLoading = state === undefined;

  return isLoading ? (
    <Box
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Stack
      sx={{
        gap: "1rem",
      }}
    >
      <SectionTitle
        titleText={titleText}
        subtitleText={
          NHSNumber
            ? `NHS Number: ${NhsNumberSchema.catch(t("common.unknown")).parse(NHSNumber)}`
            : undefined
        }
      ></SectionTitle>
      {state?.type === "error" ? (
        <Banner bannerType={BannerList.ERROR} title={state.error} />
      ) : state?.type === "not-found" ? (
        <Banner
          bannerType={BannerList.ERROR}
          body={t(
            "components.externalPatientLink.reviewNewExternalPatientRecord.banner.notFound",
          ).split("\n")}
        />
      ) : state?.type === "mergedExternalPatientLink" ? (
        <Banner
          bannerType={BannerList.WARNING}
          body={t(
            "components.externalPatientLink.reviewNewExternalPatientRecord.banner.mergedExternalPatientLink",
          ).split("\n")}
        />
      ) : state?.type === "newPatient" ? (
        <Banner
          bannerType={BannerList.INFO}
          body={t(
            "components.externalPatientLink.reviewNewExternalPatientRecord.banner.newPatient",
          ).split("\n")}
        />
      ) : null}
      {patientRecords !== undefined && state?.type === "newPatient" && (
        <Stack direction="row">
          <Box sx={{ flexGrow: 1 }} />
          <ReviewNewExternalPatientRecord
            externalSystemType={state.externalSystemType}
            disabled={creatingPatient}
            patientRecords={patientRecords}
            onCancel={onCancel}
            onConfirm={onConfirm}
          />
          <Box sx={{ flexGrow: 1 }} />
        </Stack>
      )}
      {state?.type === "mergedExternalPatientLink" && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "center",
          }}
        >
          <Button
            label={t("buttonLabels.close")}
            testId="close-button"
            onClick={() => {
              navigate(routeFns.patientHome(state.rootPatientId), {
                replace: true,
              });
            }}
          />
        </Stack>
      )}
      {state?.type === "not-found" && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "center",
          }}
        >
          <Button
            label={t("buttonLabels.backSearch")}
            testId="close-button"
            onClick={() => {
              navigate(routeFns.patientSearch(), { replace: true });
            }}
          />
        </Stack>
      )}
      {state?.type === "error" && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "center",
          }}
        >
          <Button
            label={t("buttonLabels.home")}
            testId="close-button"
            onClick={() => {
              navigate(routeFns.home(), { replace: true });
            }}
          />
        </Stack>
      )}
    </Stack>
  );
}
